import React, { useEffect, useState } from "react"
import styled from "styled-components"
import { BASE_URL, GTM_EVENT_CATEGORY } from "./constants"

import Title from "@airasia/phoenix-components/components/Title"
import InternalLinkingBlock from "../components/internalLinkingBlock"

import { withPrefix } from "gatsby"

const createObjectForInternalLinkingBlock = (
  object,
  translations,
  localeUrlSuffix
) => {
  let url = object.url + localeUrlSuffix

  return {
    href: BASE_URL + withPrefix(url),
    label: `${translations.labelText} ${object.name}`,
  }
}

const ContentWrapper = styled.div`
  margin: 5% 16px;

  @media only screen and (min-width: 720px) {
    margin: 2% 8px;
  }
`

/**
 *
 * @param {Template type from CS} template
 * @param {File generated at build time} internalLinkFile
 * @param {Internal links heading object from CS} internalLinkHeadings
 * @param {Id of the country} countryId
 * @returns Internal Links in JSX format
 *
 * The switch case comparison compares with Constants from CS. Any changes have to be reflected here
 */

export default function getInternalLinksForTemplate(obj) {
  const { template } = obj

  switch (template) {
    case "Country":
      return getLinksForCountry(obj)
    case "State":
      return getLinksForState(obj)
    case "City":
      return getLinksForCity(obj)
    case "District":
      return getLinksForDistrict(obj)
    case "Landmark":
      return getLinksForLandmark(obj)
  }
}

function getLinksForCountry({
  internalLinkFile,
  internalLinkHeadings,
  countryId,
  translations,
  localeUrlSuffix,
}) {
  let cityInternalLinks = [],
    otherCountryInternalLinks = [],
    districtInternalLinks = [],
    landmarkInternalLinks = []

  let countryData = internalLinkFile.filter(country => country.id === countryId)

  countryData = countryData.length >= 1 ? countryData[0] : {}
  countryData.cities = countryData.cities || []
  countryData.districts = countryData.districts || []
  countryData.landmarks = countryData.landmarks || []

  cityInternalLinks = countryData.cities
    .slice(0, 32)
    .map(city =>
      createObjectForInternalLinkingBlock(city, translations, localeUrlSuffix)
    )

  otherCountryInternalLinks = internalLinkFile
    .filter(country => country.id !== countryId)
    .map(country =>
      createObjectForInternalLinkingBlock(
        country,
        translations,
        localeUrlSuffix
      )
    )

  districtInternalLinks = countryData.districts
    .slice(0, 32)
    .map(district =>
      createObjectForInternalLinkingBlock(
        district,
        translations,
        localeUrlSuffix
      )
    )

  landmarkInternalLinks = countryData.landmarks
    .slice(0, 32)
    .map(landmark =>
      createObjectForInternalLinkingBlock(
        landmark,
        translations,
        localeUrlSuffix
      )
    )

  return (
    <>
      {cityInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Cities_In_Location}</Title>
          <InternalLinkingBlock
            linkObjects={cityInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.CITY_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}

      {districtInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Districts_In_Location}</Title>
          <InternalLinkingBlock
            linkObjects={districtInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.DISTRICT_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}

      {landmarkInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Landmarks_In_Location}</Title>
          <InternalLinkingBlock
            linkObjects={landmarkInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.LANDMARK_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}

      {otherCountryInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_Countries}</Title>
          <InternalLinkingBlock
            linkObjects={otherCountryInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.COUNTRY_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}
    </>
  )
}

function getLinksForState({
  internalLinkFile,
  internalLinkHeadings,
  countryId,
  translations,
  localeUrlSuffix,
}) {
  let cityInternalLinks = [],
    otherStateInternalLinks = []

  let countryData = internalLinkFile.filter(country => country.id === countryId)
  countryData = countryData.length >= 1 ? countryData[0] : {}
  countryData.cities = countryData.cities || []
  countryData.states = countryData.states || []

  cityInternalLinks = countryData.cities
    .slice(0, 32)
    .map(city =>
      createObjectForInternalLinkingBlock(city, translations, localeUrlSuffix)
    )

  otherStateInternalLinks = countryData.states
    .slice(0, 32)
    .map(state =>
      createObjectForInternalLinkingBlock(state, translations, localeUrlSuffix)
    )

  return (
    <>
      {cityInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_City}</Title>
          <InternalLinkingBlock
            linkObjects={cityInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.CITY_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}

      {otherStateInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_State}</Title>
          <InternalLinkingBlock
            linkObjects={otherStateInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.STATE_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}
    </>
  )
}

function getLinksForCity({
  internalLinkFile,
  internalLinkHeadings,
  countryId,
  translations,
  localeUrlSuffix,
}) {
  let otherCityInternalLinks = []

  let countryData = internalLinkFile.filter(country => country.id === countryId)

  countryData = countryData.length >= 1 ? countryData[0] : {}
  countryData.cities = countryData.cities || []

  otherCityInternalLinks = countryData.cities
    .slice(0, 32)
    .map(city =>
      createObjectForInternalLinkingBlock(city, translations, localeUrlSuffix)
    )

  return (
    <>
      {otherCityInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_City}</Title>
          <InternalLinkingBlock
            linkObjects={otherCityInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.CITY_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}
    </>
  )
}

function getLinksForDistrict({
  internalLinkFile,
  internalLinkHeadings,
  countryId,
  translations,
  localeUrlSuffix,
}) {
  let otherDistrictInternalLinks = []

  let countryData = internalLinkFile.filter(country => country.id === countryId)

  countryData = countryData.length >= 1 ? countryData[0] : {}
  countryData.cities = countryData.cities || []
  countryData.districts = countryData.districts || []

  otherDistrictInternalLinks = countryData.districts
    .slice(0, 32)
    .map(district =>
      createObjectForInternalLinkingBlock(
        district,
        translations,
        localeUrlSuffix
      )
    )

  return (
    <>
      {otherDistrictInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_District}</Title>
          <InternalLinkingBlock
            linkObjects={otherDistrictInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.DISTRICT_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}
    </>
  )
}

function getLinksForLandmark({
  internalLinkFile,
  internalLinkHeadings,
  countryId,
  translations,
  localeUrlSuffix,
}) {
  let countryData = internalLinkFile.filter(country => country.id === countryId)

  countryData = countryData.length >= 1 ? countryData[0] : {}
  countryData.cities = countryData.cities || []
  countryData.districts = countryData.districts || []
  countryData.landmarks = countryData.landmarks || []

  let districtInternalLinks = countryData.districts
    .slice(0, 32)
    .map(district =>
      createObjectForInternalLinkingBlock(
        district,
        translations,
        localeUrlSuffix
      )
    )

  let landmarkInternalLinks = countryData.landmarks
    .slice(0, 32)
    .map(landmark =>
      createObjectForInternalLinkingBlock(
        landmark,
        translations,
        localeUrlSuffix
      )
    )

  return (
    <>
      {districtInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_District}</Title>
          <InternalLinkingBlock
            linkObjects={districtInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.DISTRICT_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}

      {landmarkInternalLinks.length ? (
        <ContentWrapper>
          <Title type="h2">{internalLinkHeadings.Other_Landmark}</Title>
          <InternalLinkingBlock
            linkObjects={landmarkInternalLinks}
            gtmConsumerName={GTM_EVENT_CATEGORY.LANDMARK_LOCATION}
            translations={translations}
          />
        </ContentWrapper>
      ) : null}
    </>
  )
}
